import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { newId } from '@odr/shared';

import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Modal,
  Textarea,
} from '../../components/ui';
import { ApiError, api } from '../../lib/api';

/**
 * Components of one piece of equipment.
 *
 * why this is worth managing properly rather than leaving as free text: a
 * Problem Tag records which component failed, and recurrence detection groups
 * by it. A machine with no components means every finding lands at
 * "Machine level" and the repeat-offender analysis has nothing to group on.
 */

interface Component {
  id: string;
  code: string;
  name: string;
  componentType: string | null;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
}

export function ComponentsCard({
  machineId,
  canManage,
}: {
  machineId: string;
  canManage: boolean;
}) {
  const [editing, setEditing] = useState<Component | 'new' | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['machine', machineId, 'components'],
    queryFn: () =>
      api.get<{ data: Component[] }>(`/api/machines/${machineId}/components?includeInactive=1`),
  });

  const components = data?.data ?? [];

  return (
    <Card>
      <CardHeader
        title={`Components (${components.filter((c) => c.isActive).length})`}
        subtitle="What Problem Tags are filed against, and what recurrence groups by"
        action={
          canManage ? (
            <Button size="sm" variant="secondary" icon={Plus} onClick={() => setEditing('new')}>
              Add
            </Button>
          ) : null
        }
      />

      {isLoading ? (
        <div className="px-4 py-6 text-sm text-steel-500">Loading…</div>
      ) : components.length === 0 ? (
        <EmptyState
          icon={Boxes}
          title="No components yet"
          message={
            canManage
              ? 'Add the parts that fail — sealing jaws, nozzles, sensors. Without them every Problem Tag lands at machine level and recurring issues cannot be grouped.'
              : 'No components have been recorded for this equipment yet.'
          }
        />
      ) : (
        <ul className="divide-y divide-steel-100">
          {components.map((component) => (
            <li
              key={component.id}
              className="flex items-start justify-between gap-3 px-4 py-3"
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold text-steel-900">
                  <span className="font-mono text-xs text-steel-500">{component.code}</span>{' '}
                  {component.name}
                  {!component.isActive ? (
                    <Badge tone="neutral" className="ml-2">
                      Inactive
                    </Badge>
                  ) : null}
                </p>
                <p className="text-xs text-steel-500">
                  {component.componentType ?? 'Component'}
                  {component.description ? ` · ${component.description}` : ''}
                </p>
              </div>
              {canManage ? (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={Pencil}
                  aria-label={`Edit ${component.name}`}
                  onClick={() => setEditing(component)}
                />
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {editing !== null ? (
        <ComponentModal
          machineId={machineId}
          existing={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </Card>
  );
}

function ComponentModal({
  machineId,
  existing,
  onClose,
}: {
  machineId: string;
  existing?: Component;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const isEdit = existing !== undefined;
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    code: existing?.code ?? '',
    name: existing?.name ?? '',
    componentType: existing?.componentType ?? '',
    description: existing?.description ?? '',
    isActive: existing?.isActive ?? true,
  });

  const set = (key: keyof typeof form) => (value: string | boolean) =>
    setForm((current) => ({ ...current, [key]: value }));

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name: form.name.trim(),
        componentType: form.componentType.trim() || null,
        description: form.description.trim() || null,
      };
      if (isEdit) {
        // The code identifies the component on tags already filed against it.
        await api.patch(`/api/machines/components/${existing.id}`, {
          ...body,
          isActive: form.isActive,
        });
      } else {
        await api.post(`/api/machines/${machineId}/components`, {
          ...body,
          id: newId(),
          code: form.code.trim().toUpperCase(),
        });
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries();
      onClose();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not save the component.'),
  });

  const ready = form.name.trim().length > 1 && (isEdit || form.code.trim().length > 1);

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? `Edit ${existing.code}` : 'Add component'}
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button fullWidth loading={save.isPending} disabled={!ready} onClick={() => save.mutate()}>
            Save
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error ? <Callout tone="alarm">{error}</Callout> : null}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label="Code"
            required
            hint={isEdit ? 'Fixed once created.' : 'Short, e.g. SEALING_JAWS.'}
          >
            <Input
              value={form.code}
              disabled={isEdit}
              placeholder="SEALING_JAWS"
              onChange={(e) => set('code')(e.target.value.toUpperCase())}
            />
          </Field>
          <Field label="Name" required>
            <Input
              value={form.name}
              placeholder="Sealing Jaws"
              onChange={(e) => set('name')(e.target.value)}
            />
          </Field>
        </div>

        <Field label="Type">
          <Input
            value={form.componentType}
            placeholder="Sealing / Cutting / Sensor"
            onChange={(e) => set('componentType')(e.target.value)}
          />
        </Field>

        <Field label="Notes">
          <Textarea
            rows={2}
            value={form.description}
            onChange={(e) => set('description')(e.target.value)}
          />
        </Field>

        {isEdit ? (
          <>
            <label className="flex items-center gap-2 text-sm text-steel-700">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => set('isActive')(e.target.checked)}
                className="size-4 accent-info-600"
              />
              Active
            </label>
            <Callout tone="info">
              Deactivating hides this component from new Problem Tags. Tags already filed
              against it keep their history — nothing is deleted.
            </Callout>
          </>
        ) : null}
      </div>
    </Modal>
  );
}
